import { useEffect, useState } from 'react';
import { inventoryApi, type InventoryBalance } from '../../api/inventory';

export function InventoryDashboardPage() {
  const [items, setItems] = useState<InventoryBalance[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    void inventoryApi.listInventory().then(setItems).catch((e) => setError(String(e)));
  }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Inventory Dashboard</h1>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b bg-slate-50">
              <th className="p-3">Product</th>
              <th>Warehouse</th>
              <th>On hand</th>
              <th>Available</th>
              <th>Reserved</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-b">
                <td className="p-3 font-mono text-xs">{row.product_id}</td>
                <td className="font-mono text-xs">{row.warehouse_id}</td>
                <td>{row.quantity.on_hand}</td>
                <td>{row.quantity.available}</td>
                <td>{row.quantity.reserved}</td>
                <td>{row.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && !error && <p className="p-4 text-sm text-slate-500">No inventory balances.</p>}
      </div>
    </div>
  );
}

export function InventoryLedgerPage() {
  const [items, setItems] = useState<InventoryBalance[]>([]);
  const [productId, setProductId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [quantity, setQuantity] = useState('0');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const reload = () => void inventoryApi.listInventory().then(setItems).catch((e) => setError(String(e)));

  useEffect(() => { reload(); }, []);

  const onAdjust = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      await inventoryApi.adjustInventory({
        product_id: productId,
        warehouse_id: warehouseId,
        quantity: Number(quantity),
        reason,
      });
      setMessage('Adjustment recorded.');
      reload();
    } catch (err) {
      setError(String(err));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Stock Ledger & Adjustments</h1>
      <form onSubmit={onAdjust} className="mt-6 grid gap-3 rounded-xl border bg-white p-4 sm:grid-cols-2">
        <input className="rounded border px-3 py-2 text-sm" placeholder="Product ID" value={productId} onChange={(e) => setProductId(e.target.value)} required />
        <input className="rounded border px-3 py-2 text-sm" placeholder="Warehouse ID" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required />
        <input className="rounded border px-3 py-2 text-sm" type="number" step="any" placeholder="Quantity delta" value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
        <input className="rounded border px-3 py-2 text-sm" placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        <button type="submit" className="rounded bg-slate-900 px-4 py-2 text-sm text-white sm:col-span-2">Post adjustment</button>
      </form>
      {message && <p className="mt-4 text-sm text-green-700">{message}</p>}
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <p className="mt-6 text-sm text-slate-600">Current balances ({items.length} rows)</p>
    </div>
  );
}

export function MarketplaceInventoryPage() {
  const [items, setItems] = useState<InventoryBalance[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    void inventoryApi.listInventory().then(setItems).catch((e) => setError(String(e)));
  }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Marketplace Inventory</h1>
      <p className="mt-1 text-sm text-slate-600">Cross-channel stock visibility for Amazon, Flipkart, Shopify and warehouse sync.</p>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {['Amazon FBA', 'Amazon MFN', 'Shopify'].map((channel) => (
          <div key={channel} className="rounded-xl border bg-white p-4">
            <p className="font-medium">{channel}</p>
            <p className="mt-2 text-2xl font-semibold">{items.length}</p>
            <p className="text-xs text-slate-500">mapped SKUs</p>
          </div>
        ))}
      </div>
    </div>
  );
}
